/**
 * This code was  originally copied from the lexical playground source code on github
 * And so it doesn't follow our code style
 * https://github.com/facebook/lexical/tree/main/packages/lexical-playground
 *
 */
import "./RepeatablePlugin.css";

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $findMatchingParent, $insertNodeToNearestRoot, mergeRegister } from "@lexical/utils";
import { $dfs, $filter, addClassNamesToElement } from "@lexical/utils";
import { PortalType } from "contexts/portal.context";
import type { ElementNode, LexicalNode, ParagraphNode } from "lexical";
import {
    $createParagraphNode,
    $createTextNode,
    $getPreviousSelection,
    $getRoot,
    $getSelection,
    $isElementNode,
    $isParagraphNode,
    $isRangeSelection,
    $isRootNode,
    $nodesOfType,
    $parseSerializedNode,
    $setSelection,
    COMMAND_PRIORITY_LOW,
    createCommand,
    DELETE_CHARACTER_COMMAND,
    INSERT_PARAGRAPH_COMMAND,
    KEY_ARROW_DOWN_COMMAND,
    KEY_ARROW_LEFT_COMMAND,
    KEY_ARROW_RIGHT_COMMAND,
    KEY_ARROW_UP_COMMAND,
    KEY_DELETE_COMMAND,
    KEY_DOWN_COMMAND,
    NodeKey,
    RootNode,
    SELECTION_CHANGE_COMMAND,
    TextNode,
} from "lexical";
import { useContext, useEffect } from "react";

import type { TArticlesFormData, TBeneficiariesFormData } from "..";
import { expandArticleRepeatableBlock, expandBeneficiaryRepeatableBlock } from "./expandRepeatableBlocks";
import {
    $createRepeatableContainerNode,
    $isRepeatableContainerNode,
    RepeatableContainerNode,
} from "./RepeatableContainerNode";
import { $createRepeatableContentNode, $isRepeatableContentNode, RepeatableContentNode } from "./RepeatableContentNode";
import { $createRepeatableTitleNode, $isRepeatableTitleNode, RepeatableTitleNode } from "./RepeatableTitleNode";
import type { RepeatBy } from "./repeatBy";

export type InsertRepeatablePayload = {
    nodes: ParagraphNode | LexicalNode[];
    repeatBy?: RepeatBy;
};

export const INSERT_REPEATABLE_COMMAND = createCommand<InsertRepeatablePayload>();

interface IRepeatablePlugin {
    /**
     * Selected beneficiaries for multi-signature data
     */
    beneficiariesFormData: TBeneficiariesFormData | undefined;
    /**
     * Articles of the work, ordered by id. Present when the agreement is created.
     */
    articlesFormData?: TArticlesFormData;
}

/**
 * This plugin allow to create repeatable blocks. Repeatable blocks are used for multi-signature contracts.
 * A block contain variables and is repeated for all beneficiaries
 */
export default function RepeatablePlugin({ beneficiariesFormData, articlesFormData }: IRepeatablePlugin): any {
    const [editor] = useLexicalComposerContext();

    const hasBeneficiaries = !!beneficiariesFormData?.beneficiaryIds?.length;
    const articles = articlesFormData?.articles;
    const hasArticles = !!articles?.length;

    useEffect(() => {
        if (!editor.hasNodes([RepeatableContainerNode, RepeatableContentNode, RepeatableTitleNode])) {
            throw new Error(
                "RepeatablePlugin: RepeatableContainerNode, RepeatableContentNode or RepeatableTitleNode not registered on editor",
            );
        }
        /**
         * Agreement setup copies each block once per beneficiary or once per work article.
         * The editor is mounted only after those lists are known, so this runs once.
         */
        if (hasBeneficiaries || hasArticles) {
            editor.update(() => {
                const allNodes = $dfs();
                const repeatableBlocks = allNodes.filter((node) => $isRepeatableContainerNode(node.node)) as Readonly<{
                    depth: number;
                    node: RepeatableContainerNode;
                }>[];
                repeatableBlocks.forEach((block) => {
                    if (block.node.getRepeatBy() === "article") {
                        expandArticleRepeatableBlock(block.node, articles);
                        return;
                    }
                    expandBeneficiaryRepeatableBlock(block.node, beneficiariesFormData);
                });
            });
        }

        const onEscapeUp = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.offset === 0) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isRepeatableContainerNode);

                if ($isRepeatableContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (
                        parent !== null &&
                        parent.getFirstChild<LexicalNode>() === container &&
                        selection.anchor.key === container.getFirstDescendant<LexicalNode>()?.getKey()
                    ) {
                        container.insertBefore($createParagraphNode());
                    }
                }
            }

            return false;
        };

        const onEscapeDown = () => {
            const selection = $getSelection();
            if ($isRangeSelection(selection) && selection.isCollapsed()) {
                const container = $findMatchingParent(selection.anchor.getNode(), $isRepeatableContainerNode);

                if ($isRepeatableContainerNode(container)) {
                    const parent = container.getParent<ElementNode>();
                    if (parent !== null && parent.getLastChild<LexicalNode>() === container) {
                        const titleParagraph = container.getFirstDescendant<LexicalNode>();
                        const contentParagraph = container.getLastDescendant<LexicalNode>();

                        if (
                            (contentParagraph !== null &&
                                selection.anchor.key === contentParagraph.getKey() &&
                                selection.anchor.offset === contentParagraph.getTextContentSize()) ||
                            (titleParagraph !== null &&
                                selection.anchor.key === titleParagraph.getKey() &&
                                selection.anchor.offset === titleParagraph.getTextContentSize())
                        ) {
                            container.insertAfter($createParagraphNode());
                        }
                    }
                }
            }

            return false;
        };

        return mergeRegister(
            editor.registerNodeTransform(RepeatableContentNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isRepeatableContainerNode(parent)) {
                    const children = node.getChildren<LexicalNode>();
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),

            editor.registerNodeTransform(RepeatableTitleNode, (node) => {
                const parent = node.getParent<ElementNode>();
                if (!$isRepeatableContainerNode(parent)) {
                    node.replace($createParagraphNode().append(...node.getChildren<LexicalNode>()));
                    return;
                }
            }),

            editor.registerNodeTransform(RepeatableContainerNode, (node) => {
                const children = node.getChildren<LexicalNode>();
                if (
                    children.length !== 2 ||
                    !$isRepeatableTitleNode(children[0]) ||
                    !$isRepeatableContentNode(children[1])
                ) {
                    for (const child of children) {
                        node.insertBefore(child);
                    }
                    node.remove();
                }
            }),
            // When collapsible conditional is the last child pressing down/right arrow will insert paragraph
            // below it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if trailing paragraph is accidentally deleted
            editor.registerCommand(KEY_ARROW_DOWN_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            editor.registerCommand(KEY_ARROW_RIGHT_COMMAND, onEscapeDown, COMMAND_PRIORITY_LOW),

            // When collapsible conditional is the first child pressing up/left arrow will insert paragraph
            // above it to allow adding more content. It's similar what $insertBlockNode
            // (mainly for decorators), except it'll always be possible to continue adding
            // new content even if leading paragraph is accidentally deleted
            editor.registerCommand(KEY_ARROW_UP_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            editor.registerCommand(KEY_ARROW_LEFT_COMMAND, onEscapeUp, COMMAND_PRIORITY_LOW),

            editor.registerCommand(
                DELETE_CHARACTER_COMMAND,
                () => {
                    const selection = $getSelection();
                    if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
                        return false;
                    }
                    const selectionNode = selection.anchor.getNode();
                    const repeatableContainerNode = $findMatchingParent(selectionNode, $isRepeatableContainerNode);
                    const repeatableContentNode = $findMatchingParent(selectionNode, $isRepeatableContentNode);
                    const newParagraphNode = $createParagraphNode().append($createTextNode(""));
                    const repeatableTextNodes = repeatableContainerNode?.getAllTextNodes();
                    /**
                     * If repeatableContainerNode :
                     * -> is a repeatable node
                     * -> doesn't have more than 1 line of children
                     * -> doesn't have textNodes children
                     * so delete repeatable block
                     */
                    const hasEmptyContent =
                        repeatableContentNode &&
                        repeatableContentNode.getChildren().length < 2 &&
                        !repeatableTextNodes?.length;

                    if (repeatableContainerNode && hasEmptyContent) {
                        $setSelection(null);
                        repeatableContainerNode?.replace(newParagraphNode);
                    }

                    const topLevelElement = selectionNode.getTopLevelElement();
                    if (topLevelElement === null) {
                        return false;
                    }

                    const container = topLevelElement.getPreviousSibling<LexicalNode>();
                    if (!$isRepeatableContainerNode(container) || container.getOpen()) {
                        return false;
                    }

                    container.setOpen(true);
                    return false;
                },
                COMMAND_PRIORITY_LOW,
            ),
            editor.registerCommand(
                INSERT_REPEATABLE_COMMAND,
                ({ nodes, repeatBy = "beneficiary" }) => {
                    editor.update(() => {
                        const title = $createRepeatableTitleNode();
                        const repeatableContentNode = $createRepeatableContentNode(undefined, undefined, repeatBy);
                        // check if it's inline selection -> so append the selected paragraph
                        if ($isParagraphNode(nodes as ParagraphNode)) {
                            repeatableContentNode.append(nodes as ParagraphNode);
                        } else {
                            (nodes as ParagraphNode[]).forEach((paragraph) => {
                                repeatableContentNode.append(paragraph);
                            });
                        }
                        $insertNodeToNearestRoot(
                            $createRepeatableContainerNode(true, false, repeatBy).append(title, repeatableContentNode),
                        );
                        $setSelection(null);

                        const newRepeatableContainer = $findMatchingParent(
                            repeatableContentNode,
                            $isRepeatableContainerNode,
                        );
                        const nextSibling = newRepeatableContainer?.getNextSibling();
                        // sometimes it adds an empty uncontroled paragraph
                        if ($isParagraphNode(nextSibling) && !nextSibling.getTextContent()) {
                            nextSibling.remove();
                        }
                        newRepeatableContainer?.insertAfter($createParagraphNode());
                    });
                    return true;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
        // Lists are ready on the first mount (the setup screen waits for the work articles).
        // Re-running this effect would duplicate blocks that were already expanded.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editor]);
}
